import { Component, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { TimeEntry } from '../../../shared/models/activity.model';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user.model';
import { ProjectService } from '../../../shared/services/project.service';
import { ProjectStage } from '../../../shared/models/project.model';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';

type PeriodMode = 'day' | 'week' | 'month' | 'quarter' | 'all';
type AggregationMode = 'project' | 'phase';

interface ProjectSegment {
  name: string;
  hours: number;
  color: string;
  isTask: boolean;
}

interface DayBucket {
  date: Date;
  dateKey: string;        // 'YYYY-MM-DD'
  label: string;          // display label (e.g. 'Lun 14', '14 avr.')
  approvedHours: number;
  draftHours: number;
  totalHours: number;
  entries: TimeEntry[];
  projectSegments: ProjectSegment[];
}

interface ProjectBreakdown {
  name: string;
  hours: number;
  approvedHours: number;
  draftHours: number;
  entries: number;
  type: 'project' | 'task';
  color: string;
}

@Component({
  selector: 'app-reporting-board',
  standalone: true,
  imports: [CommonModule, FormsModule, PDFExportModule],
  providers: [DatePipe],
  templateUrl: './reporting-board.html',
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ width: '0px', opacity: 0, overflow: 'hidden' }),
        animate('250ms ease-out', style({ width: '340px', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ width: '340px', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ width: '0px', opacity: 0 }))
      ])
    ])
  ],
  styles: [`
    :host {
      display: block;
      width: 100%;
      overflow: visible;
    }
  `]
})
export class ReportingBoardComponent implements OnInit {
  private datePipe = inject(DatePipe);
  private timeEntryService = inject(TimeEntriesService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private elRef = inject(ElementRef);

  // ── State ───────────────────────────────────────────────────────────────
  loading = true;
  employees: User[] = [];
  /** Archived employees are hidden from the employee filter by default. */
  showArchivedEmployees = false;

  /**
   * Employees offered in the filter: active ones, plus archived ones when the
   * admin ticks the box, plus whoever is currently selected.
   * `employees` itself keeps everybody so archived names still resolve.
   */
  get visibleEmployees(): User[] {
    return this.employees.filter(e =>
      !e.isArchived ||
      this.showArchivedEmployees ||
      (!!this.selectedEmployeeId && e.cognitoId === this.selectedEmployeeId)
    );
  }

  get archivedEmployeeCount(): number {
    return this.employees.filter(e => !!e.isArchived).length;
  }

  periodMode: PeriodMode = 'month';
  currentDate = new Date(); // anchor date for navigation

  // Filters
  selectedEmployeeId = '';
  approvalStatusFilter = '';

  // Project multi-select
  selectedProjects: Set<string> = new Set();
  projectSearch = '';
  projectDropdownOpen = false;
  allKnownProjectNames: Set<string> = new Set();
  projectIdToName: Map<string, string> = new Map();

  // Aggregation mode
  aggregationMode: AggregationMode = 'project';
  private userOverrodeAggregation = false;

  // Data
  allEntries: TimeEntry[] = [];
  dayBuckets: DayBucket[] = [];
  selectedDay: DayBucket | null = null;
  projectBreakdowns: ProjectBreakdown[] = [];

  // Summary
  todayHours = 0;
  weekHours = 0;
  monthHours = 0;
  weekTarget = 40;
  monthTarget = 160; // 4 weeks × 40h
  approvedTotal = 0;
  draftTotal = 0;
  totalHours = 0;

  // Chart
  chartMaxHours = 8; // auto-scaled

  // Deterministic color palette for projects
  private readonly PROJECT_COLORS = [
    '#6366f1', // indigo
    '#f59e0b', // amber
    '#10b981', // emerald
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#f97316', // orange
    '#ec4899', // pink
    '#14b8a6', // teal
    '#84cc16', // lime
    '#3b82f6', // blue
    '#a855f7', // purple
  ];
  private projectColorCache = new Map<string, string>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.projectDropdownOpen && !this.elRef.nativeElement.querySelector('.project-filter-container')?.contains(event.target)) {
      this.projectDropdownOpen = false;
    }
  }

  // Stage data for color lookup
  private allStages: ProjectStage[] = [];
  private stageColorMap = new Map<string, string>(); // stageName -> color

  async ngOnInit() {
    // Initialize services to ensure employees and projects are loaded
    await Promise.all([
      this.userService.init(),
      this.projectService.listProjects(),
    ]);

    this.userService.users$.subscribe(emps => {
      this.employees = emps;
    });
    this.projectService.projects$.subscribe(projects => {
      projects.forEach(p => {
        if (p.projectId) {
          this.projectIdToName.set(p.projectId, p.projectName);
          this.allKnownProjectNames.add(p.projectId);
        }
      });
    });
    this.projectService.projectsStage$.subscribe(stages => {
      this.allStages = stages;
      this.stageColorMap.clear();
      stages.forEach(s => {
        if (s.stageName && s.color) {
          this.stageColorMap.set(s.stageName, s.color);
        }
      });
    });
    await this.loadData();
  }

  // ── Period helpers ──────────────────────────────────────────────────────

  get periodLabel(): string {
    const d = this.currentDate;
    switch (this.periodMode) {
      case 'day': return this.datePipe.transform(d, 'EEEE d MMMM yyyy') ?? '';
      case 'week': {
        const { start, end } = this.getWeekRange(d);
        const s = this.datePipe.transform(start, 'd MMM') ?? '';
        const e = this.datePipe.transform(end, 'd MMM yyyy') ?? '';
        const weekNum = this.getWeekNumber(d);
        return `Sem. ${weekNum} — ${s} – ${e}`;
      }
      case 'month': return this.datePipe.transform(d, 'MMMM yyyy') ?? '';
      case 'quarter': {
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `Q${q} ${d.getFullYear()}`;
      }
      case 'all': return 'Tout l\'historique';
    }
  }

  get periodRange(): { start: Date; end: Date } {
    const d = this.currentDate;
    switch (this.periodMode) {
      case 'day':
        return {
          start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0),
          end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
        };
      case 'week':
        return this.getWeekRange(d);
      case 'month':
        return {
          start: new Date(d.getFullYear(), d.getMonth(), 1),
          end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
        };
      case 'quarter': {
        const qStart = Math.floor(d.getMonth() / 3) * 3;
        return {
          start: new Date(d.getFullYear(), qStart, 1),
          end: new Date(d.getFullYear(), qStart + 3, 0, 23, 59, 59, 999)
        };
      }
      case 'all':
        // Not used for fetching in 'all' mode (GSI query), but needed by buildBuckets
        return { start: new Date(2020, 0, 1), end: new Date() };
    }
  }

  private getWeekRange(d: Date): { start: Date; end: Date } {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    const start = new Date(d.getFullYear(), d.getMonth(), diff, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  private getWeekNumber(d: Date): number {
    const target = new Date(d.valueOf());
    const dayNr = (d.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  }

  /** Whether the 'All' period option should be visible (needs exactly 1 project selected) */
  get canShowAllPeriod(): boolean {
    return this.selectedProjects.size === 1;
  }

  setPeriod(mode: PeriodMode) {
    this.periodMode = mode;
    this.selectedDay = null;
    this.loadData();
  }

  navigatePeriod(direction: -1 | 1) {
    if (this.periodMode === 'all') return; // no navigation in 'all' mode
    const d = new Date(this.currentDate);
    switch (this.periodMode) {
      case 'day': d.setDate(d.getDate() + direction); break;
      case 'week': d.setDate(d.getDate() + direction * 7); break;
      case 'month': d.setMonth(d.getMonth() + direction); break;
      case 'quarter': d.setMonth(d.getMonth() + direction * 3); break;
    }
    this.currentDate = d;
    this.selectedDay = null;
    this.loadData();
  }

  goToToday() {
    this.currentDate = new Date();
    this.selectedDay = null;
    this.loadData();
  }

  // ── Data loading ───────────────────────────────────────────────────────

  async loadData() {
    this.loading = true;
    try {
      // ── 'all' mode: fetch full history for the selected project ──
      if (this.periodMode === 'all' && this.canShowAllPeriod) {
        const key = this.selectedProjects.values().next().value!;
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        let entries: TimeEntry[];

        if (uuidPattern.test(key)) {
          // UUID key → use the efficient byProjectId GSI
          const projectName = this.projectIdToName.get(key);
          console.log('[ReportingBoard] Fetching ALL entries via GSI for project:', key);
          entries = await this.timeEntryService.fetchAllByProjectId(key, projectName);
        } else {
          // Name-based key → wide date range scan with project filter
          console.log('[ReportingBoard] Fetching ALL entries via scan for project:', key);
          const filters: any = {};
          if (key.startsWith('[Task] ')) {
            filters.crmTaskNames = [key];
          } else {
            filters.baseProjectNames = [key];
          }
          entries = await this.timeEntryService.fetchAllEntries(filters);
        }

        // Apply client-side sub-filters
        if (this.selectedEmployeeId) {
          entries = entries.filter(e => e.employeeId === this.selectedEmployeeId);
        }
        if (this.approvalStatusFilter) {
          entries = entries.filter(e => (e.approvalStatus ?? 'DRAFT') === this.approvalStatusFilter);
        }
        this.allEntries = entries;
      } else {
        // ── Normal date-bounded fetch ──
        const { start, end } = this.periodRange;
        const filters: any = {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        };
        if (this.selectedEmployeeId) filters.employeeId = this.selectedEmployeeId;
        if (this.approvalStatusFilter) filters.approvalStatus = this.approvalStatusFilter;

        // Project filter
        if (this.selectedProjects.size > 0) {
          const keys = [...this.selectedProjects];
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const projectIds = keys.filter(k => uuidPattern.test(k));
          const crmKeys = keys.filter(k => k.startsWith('[Task] '));
          const nameKeys = keys.filter(k => !uuidPattern.test(k) && !k.startsWith('[Task] '));
          if (projectIds.length) filters.projectIds = projectIds;
          if (nameKeys.length) filters.baseProjectNames = nameKeys;
          if (crmKeys.length) filters.crmTaskNames = crmKeys;
        }

        console.log('[ReportingBoard] Fetching entries with filters:', filters);
        this.allEntries = await this.timeEntryService.fetchAllEntries(filters);
      }
      console.log('[ReportingBoard] Fetched', this.allEntries.length, 'entries');

      this.buildKnownProjects();
      this.buildBuckets();
      this.computeSummary();
      this.buildProjectBreakdowns();
    } catch (e) {
      console.error('[ReportingBoard] Error loading data:', e);
    } finally {
      this.loading = false;
    }
  }


  private buildKnownProjects() {
    this.allEntries.forEach(e => {
      const key = this.getBaseProjectKey(e);
      if (key) {
        this.allKnownProjectNames.add(key);
        if (e.projectId && !e.projectName?.startsWith('[Task] ')) {
          const baseName = (e.projectName ?? '').split(' - ')[0].trim();
          if (baseName) this.projectIdToName.set(e.projectId, baseName);
        }
      }
    });
  }

  private buildBuckets() {
    const { start, end } = this.periodRange;
    const buckets: DayBucket[] = [];
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

    if (this.periodMode === 'all') {
      // ── All: group by month, derive range from actual data ──
      if (this.allEntries.length === 0) {
        this.dayBuckets = [];
        return;
      }
      // Find the actual date range from entries
      let minDate = new Date(this.allEntries[0].startTime);
      let maxDate = new Date(this.allEntries[0].startTime);
      for (const entry of this.allEntries) {
        const d = new Date(entry.startTime);
        if (d < minDate) minDate = d;
        if (d > maxDate) maxDate = d;
      }
      // Create monthly buckets from first to last entry month
      const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const endMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      while (cursor <= endMonth) {
        const dateKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
        const label = `${monthNames[cursor.getMonth()]} ${cursor.getFullYear().toString().slice(-2)}`;
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setMonth(cursor.getMonth() + 1);
      }
      // Distribute entries into monthly buckets
      for (const entry of this.allEntries) {
        const d = new Date(entry.startTime);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const bucket = buckets.find(b => b.dateKey === monthKey);
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }
      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    } else if (this.periodMode === 'quarter') {
      // ── Quarter: group by week ──
      const cursor = new Date(start);
      // Align to Monday
      const dayOfWeek = cursor.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      cursor.setDate(cursor.getDate() + diff);

      while (cursor <= end) {
        const weekEnd = new Date(cursor);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const dateKey = this.toDateKey(cursor);
        const label = `${cursor.getDate()}/${cursor.getMonth() + 1}`;
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setDate(cursor.getDate() + 7);
      }

      // Distribute entries into weekly buckets
      for (const entry of this.allEntries) {
        const entryDate = new Date(entry.startTime);
        // Find which week bucket this entry belongs to
        const bucket = buckets.find((b, i) => {
          const nextBucket = buckets[i + 1];
          if (nextBucket) {
            return entryDate >= b.date && entryDate < nextBucket.date;
          }
          return entryDate >= b.date;
        });
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }

      // Build project segments for weekly buckets
      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    } else {
      // ── Day / Week / Month: one bucket per day ──
      const cursor = new Date(start);
      while (cursor <= end) {
        const dateKey = this.toDateKey(cursor);
        const isWeekly = this.periodMode === 'week';
        const isDay = this.periodMode === 'day';
        let label: string;
        if (isDay) {
          label = this.datePipe.transform(cursor, 'HH:mm') ?? '';
        } else if (isWeekly) {
          label = `${dayNames[cursor.getDay()]} ${cursor.getDate()}`;
        } else {
          label = `${cursor.getDate()}`;
        }
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      // Distribute entries into daily buckets
      for (const entry of this.allEntries) {
        const key = this.toDateKey(new Date(entry.startTime));
        const bucket = buckets.find(b => b.dateKey === key);
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }

      // Build project segments for daily buckets
      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    }

    // Auto-scale chart — adapt to actual data range
    const max = Math.max(...buckets.map(b => b.totalHours), 0.1);
    this.chartMaxHours = this.niceChartMax(max);

    this.dayBuckets = buckets;
  }

  private computeSummary() {
    const now = new Date();
    const todayKey = this.toDateKey(now);
    const { start: weekStart, end: weekEnd } = this.getWeekRange(now);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    let todayH = 0, weekH = 0, monthH = 0, approved = 0, draft = 0;

    for (const entry of this.allEntries) {
      const h = this.durationHours(entry);
      const entryDate = new Date(entry.startTime);
      const key = this.toDateKey(entryDate);
      const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';

      if (key === todayKey) todayH += h;
      if (entryDate >= weekStart && entryDate <= weekEnd) weekH += h;
      if (entryDate >= monthStart && entryDate <= monthEnd) monthH += h;
      if (isApproved) approved += h;
      else draft += h;
    }

    this.todayHours = todayH;
    this.weekHours = weekH;
    this.monthHours = monthH;
    this.approvedTotal = approved;
    this.draftTotal = draft;
    this.totalHours = approved + draft;
  }

  private buildProjectBreakdowns() {
    const entries = this.selectedDay ? this.selectedDay.entries : this.allEntries;
    const map = new Map<string, ProjectBreakdown>();

    for (const entry of entries) {
      const name = this.getSegmentName(entry);
      if (!map.has(name)) {
        const isTask = (entry.projectName ?? '').startsWith('[Task] ');
        map.set(name, {
          name,
          hours: 0,
          approvedHours: 0,
          draftHours: 0,
          entries: 0,
          type: isTask ? 'task' : 'project',
          color: this.getSegmentColor(entry, name),
        });
      }
      const pb = map.get(name)!;
      const h = this.durationHours(entry);
      pb.hours += h;
      pb.entries++;
      if ((entry.approvalStatus ?? 'DRAFT') === 'APPROVED') pb.approvedHours += h;
      else pb.draftHours += h;
    }

    this.projectBreakdowns = [...map.values()].sort((a, b) => b.hours - a.hours);
  }
  // ── Project color helpers ───────────────────────────────────────────────

  getProjectColor(name: string): string {
    if (this.projectColorCache.has(name)) return this.projectColorCache.get(name)!;
    // Simple deterministic hash
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    const color = this.PROJECT_COLORS[Math.abs(hash) % this.PROJECT_COLORS.length];
    this.projectColorCache.set(name, color);
    return color;
  }

  private buildProjectSegments(entries: TimeEntry[]): ProjectSegment[] {
    const map = new Map<string, ProjectSegment>();
    for (const entry of entries) {
      const name = this.getSegmentName(entry);
      if (!map.has(name)) {
        map.set(name, {
          name,
          hours: 0,
          color: this.getSegmentColor(entry, name),
          isTask: (entry.projectName ?? '').startsWith('[Task] '),
        });
      }
      map.get(name)!.hours += this.durationHours(entry);
    }
    // Sort by hours descending so largest segment is at bottom
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }

  /**
   * Round the tallest bucket up to a clean axis maximum. Steps stay small enough
   * that the tallest column reaches most of the track — the previous rule rounded
   * to 2h above 4h, which could leave the tallest bar at two thirds height.
   */
  private niceChartMax(max: number): number {
    const step = max <= 4 ? 0.5 : max <= 10 ? 1 : 2;
    return Math.max(step, Math.ceil(max / step) * step);
  }

  /**
   * Segment height as a percentage of the bar track, so the chart scales with the
   * container and lines up with the Y axis. It used to be a fixed 140px scale,
   * which left the columns squashed at the bottom of a taller chart area.
   */
  getSegmentPercent(segment: ProjectSegment, bucket: DayBucket): number {
    if (bucket.totalHours === 0 || this.chartMaxHours === 0) return 0;
    const pct = (segment.hours / this.chartMaxHours) * 100;
    return pct > 0 ? Math.max(pct, 1) : 0; // keep a sliver visible for tiny segments
  }

  // ── Chart interactions ─────────────────────────────────────────────────

  selectDay(bucket: DayBucket) {
    if (this.selectedDay?.dateKey === bucket.dateKey) {
      this.selectedDay = null;
    } else {
      this.selectedDay = bucket;
    }
    this.buildProjectBreakdowns();
  }

  clearDaySelection() {
    this.selectedDay = null;
    this.buildProjectBreakdowns();
  }

  get yAxisLabels(): number[] {
    const steps = 4;
    const labels: number[] = [];
    for (let i = steps; i >= 0; i--) {
      labels.push(Math.round((this.chartMaxHours / steps) * i * 10) / 10);
    }
    return labels;
  }

  /** Minimum pixel width for the chart bars container (enables horizontal scroll for long ranges) */
  get chartMinWidth(): number | null {
    if (this.dayBuckets.length <= 14) return null; // fits naturally
    return this.dayBuckets.length * 48; // 48px per bar minimum
  }

  isToday(bucket: DayBucket): boolean {
    return bucket.dateKey === this.toDateKey(new Date());
  }

  isWeekend(bucket: DayBucket): boolean {
    const day = bucket.date.getDay();
    return day === 0 || day === 6;
  }

  // ── Filter handlers ────────────────────────────────────────────────────

  onFilterChange() {
    this.selectedDay = null;
    this.loadData();
  }

  toggleProject(name: string) {
    const next = new Set(this.selectedProjects);
    if (next.has(name)) next.delete(name); else next.add(name);
    this.selectedProjects = next;
    this.autoSetAggregationMode();
    // If 'all' mode is no longer valid after toggle, fall back to 'month'
    if (this.periodMode === 'all' && !this.canShowAllPeriod) {
      this.periodMode = 'month';
    }
    this.loadData();
  }

  clearProjectFilter() {
    this.selectedProjects = new Set();
    this.projectSearch = '';
    this.autoSetAggregationMode();
    // Fall back from 'all' when no project is selected
    if (this.periodMode === 'all') {
      this.periodMode = 'month';
    }
    this.loadData();
  }

  setAggregationMode(mode: AggregationMode) {
    this.aggregationMode = mode;
    this.userOverrodeAggregation = true;
    this.projectColorCache.clear(); // reset colors for new grouping
    this.rebuildFromMemory();
  }

  private autoSetAggregationMode() {
    if (this.userOverrodeAggregation) return;
    // Auto-switch: 1 project selected → phase view, otherwise → project view
    this.aggregationMode = this.selectedProjects.size === 1 ? 'phase' : 'project';
    this.projectColorCache.clear();
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
  }

  // ── Approval actions ───────────────────────────────────────────────────

  async approveEntry(entry: TimeEntry) {
    // Optimistic update: instant UI change
    entry.approvalStatus = 'APPROVED';
    this.rebuildFromMemory();

    // Background: persist to backend
    try {
      await this.timeEntryService.approveEntry(entry.id!);
    } catch (e) {
      // Rollback on failure
      entry.approvalStatus = 'DRAFT';
      this.rebuildFromMemory();
      console.error('Failed to approve entry:', e);
    }
  }

  async setDraftEntry(entry: TimeEntry) {
    // Optimistic update: instant UI change
    entry.approvalStatus = 'DRAFT';
    this.rebuildFromMemory();

    // Background: persist to backend
    try {
      await this.timeEntryService.setDraftEntry(entry.id!);
    } catch (e) {
      // Rollback on failure
      entry.approvalStatus = 'APPROVED';
      this.rebuildFromMemory();
      console.error('Failed to set draft:', e);
    }
  }

  /** Rebuild chart buckets + summary from in-memory entries without refetching */
  private rebuildFromMemory() {
    const selectedDateKey = this.selectedDay?.dateKey;
    this.buildBuckets();
    this.computeSummary();
    this.buildProjectBreakdowns();
    if (selectedDateKey) {
      this.selectedDay = this.dayBuckets.find(b => b.dateKey === selectedDateKey) ?? null;
    }
  }

  // ── Utility ────────────────────────────────────────────────────────────

  private toDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  durationHours(entry: TimeEntry): number {
    const ms = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime();
    return ms / 3600000;
  }

  formatHours(h: number): string {
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
  }

  formatPercent(current: number, target: number): string {
    if (target === 0) return '0%';
    return Math.round((current / target) * 100) + '%';
  }

  getEmployeeName(id: string): string {
    const user = this.employees.find(emp => emp.cognitoId === id);
    return user ? `${user.firstName} ${user.name}` : 'Unknown';
  }

  getBaseProjectKey(entry: { projectName?: string | null; projectId?: string | null }): string {
    const name = entry.projectName ?? '';
    if (name.startsWith('[Task] ')) return name;
    if (entry.projectId) return entry.projectId;
    return name.split(' - ')[0].trim();
  }

  resolveKeyName(key: string): string {
    if (this.projectIdToName.has(key)) return this.projectIdToName.get(key)!;
    if (key.startsWith('[Task] ')) return key.replace(/^\[Task\] /, '');
    return key;
  }

  getEntryName(projectName: string | null | undefined): string {
    const name = projectName ?? '';
    return name.startsWith('[Task] ') ? name.replace(/^\[Task\] /, '') : name;
  }

  /** Get the base project name (before ' - ') */
  private getBaseProjectName(projectName: string | null | undefined): string {
    const name = projectName ?? '';
    if (name.startsWith('[Task] ')) return name.replace(/^\[Task\] /, '');
    return name.split(' - ')[0].trim() || name;
  }

  /** Get the phase/stage name (after ' - '), or the full name if no separator */
  private getPhaseName(projectName: string | null | undefined): string {
    const name = projectName ?? '';
    if (name.startsWith('[Task] ')) return name.replace(/^\[Task\] /, '');
    const parts = name.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ').trim() : name;
  }

  /** Get the segment display name based on current aggregation mode */
  getSegmentName(entry: TimeEntry): string {
    if (this.aggregationMode === 'phase') {
      // In phase mode, use the full projectName (which includes phase)
      return this.getEntryName(entry.projectName);
    } else {
      // In project mode, group by base project name (before ' - ')
      return this.getBaseProjectName(entry.projectName);
    }
  }

  /** Get the color for a segment based on current aggregation mode */
  private getSegmentColor(entry: TimeEntry, segmentName: string): string {
    if (this.aggregationMode === 'phase') {
      // In phase mode, try to use the stage's own color
      const phaseName = this.getPhaseName(entry.projectName);
      const stageColor = this.stageColorMap.get(phaseName);
      if (stageColor) return stageColor;
      // Fallback: if we have stageId, look up directly
      if (entry.stageId) {
        const stage = this.allStages.find(s => s.projectStageId === entry.stageId);
        if (stage?.color) return stage.color;
      }
    }
    // Default: deterministic hash color
    return this.getProjectColor(segmentName);
  }

  getEntryType(projectName: string | null | undefined): string {
    return (projectName ?? '').startsWith('[Task] ') ? 'CRM Task' : 'Project';
  }

  getDuration(startTime: Date | string, endTime: Date | string): string {
    const diff = new Date(endTime).getTime() - new Date(startTime).getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours < 1) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  get uniqueProjectNames(): string[] {
    return [...this.allKnownProjectNames].sort((a, b) =>
      this.resolveKeyName(a).localeCompare(this.resolveKeyName(b))
    );
  }

  get filteredProjectOptions(): string[] {
    const q = this.projectSearch.trim().toLowerCase();
    return q
      ? this.uniqueProjectNames.filter(n => this.resolveKeyName(n).toLowerCase().includes(q))
      : this.uniqueProjectNames;
  }

  get sortedEntriesForReport(): TimeEntry[] {
    return [...this.allEntries].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }

  getEntryPhaseName(entry: TimeEntry): string {
    const full = entry.projectName ?? '';
    if (full.startsWith('[Task] ')) return 'Tâche CRM';
    const parts = full.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ').trim() : '—';
  }

  get exportDate(): Date {
    return new Date();
  }

  getExportFileName(): string {
    const employee = this.selectedEmployeeId ? this.getEmployeeName(this.selectedEmployeeId) : 'Tous';
    const cleanPeriod = this.periodLabel.replace(/[\s—–/\\:*?"<>|]/g, '_');
    const cleanEmployee = employee.replace(/[\s/\\:*?"<>|]/g, '_');
    return `Reporting_${cleanEmployee}_${cleanPeriod}.pdf`;
  }

  getBreakdownsForType(mode: AggregationMode): ProjectBreakdown[] {
    const entries = this.allEntries;
    const map = new Map<string, ProjectBreakdown>();

    for (const entry of entries) {
      // Temporarily set aggregationMode to compute segment name/color
      const originalMode = this.aggregationMode;
      this.aggregationMode = mode;
      const name = this.getSegmentName(entry);
      this.aggregationMode = originalMode;

      if (!map.has(name)) {
        const isTask = (entry.projectName ?? '').startsWith('[Task] ');
        map.set(name, {
          name,
          hours: 0,
          approvedHours: 0,
          draftHours: 0,
          entries: 0,
          type: isTask ? 'task' : 'project',
          color: this.getSegmentColor(entry, name),
        });
      }
      const pb = map.get(name)!;
      const h = this.durationHours(entry);
      pb.hours += h;
      pb.entries++;
      if ((entry.approvalStatus ?? 'DRAFT') === 'APPROVED') pb.approvedHours += h;
      else pb.draftHours += h;
    }

    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }

  /**
   * Save the report. Awaits the PdfSans face first: Kendo embeds whatever the
   * browser has already loaded, and an unloaded face means a silent fallback to
   * the PDF base-14 fonts, which is what mangled the accents.
   */
  async exportPdf(pdf: { saveAs: (name: string) => void }) {
    try {
      // Every weight the exported markup uses must be loaded, or that weight
      // falls back to a base-14 font and loses its accents.
      await Promise.all(
        ['400', '600', '700', '800'].map(w => (document as any).fonts?.load?.(`${w} 12px PdfSans`))
      );
      await (document as any).fonts?.ready;
    } catch (e) {
      console.warn('[ReportingBoard] PdfSans did not load; accents may fall back', e);
    }
    pdf.saveAs(this.getExportFileName());
  }

  triggerPrint() {
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Veuillez autoriser les fenêtres contextuelles (pop-ups) pour exporter le PDF.');
      return;
    }

    // Copy stylesheets from main document to print window
    let stylesHtml = '';
    document.querySelectorAll('link, style').forEach(node => {
      stylesHtml += node.outerHTML;
    });

    const employee = this.selectedEmployeeId ? this.getEmployeeName(this.selectedEmployeeId) : 'Tous les employés';
    const status = this.approvalStatusFilter === 'APPROVED' ? 'Approuvé' : this.approvalStatusFilter === 'DRAFT' ? 'Non approuvé' : 'Tous';
    const dateFormatted = this.datePipe.transform(this.exportDate, 'dd/MM/yyyy à HH:mm') ?? '';

    // Generate table rows HTML
    let tableRowsHtml = '';
    const sortedEntries = this.sortedEntriesForReport;
    if (sortedEntries.length === 0) {
      tableRowsHtml = `
        <tr>
          <td colspan="${this.selectedEmployeeId ? 6 : 7}" style="padding: 32px; text-align: center; color: #94a3b8; font-style: italic; border-bottom: 1px solid #f1f5f9;">
            Aucune prestation trouvée pour cette période et ces filtres.
          </td>
        </tr>
      `;
    } else {
      sortedEntries.forEach(entry => {
        const dateStr = this.datePipe.transform(entry.startTime, 'dd/MM/yyyy') ?? '';
        const empName = this.getEmployeeName(entry.employeeId!);
        const projName = this.getEntryName(entry.projectName);
        const phaseName = this.getEntryPhaseName(entry);
        const isCrmTask = this.getEntryType(entry.projectName) === 'CRM Task';
        const typeBadge = isCrmTask 
          ? `<span style="display: inline-flex; align-items: center; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; background-color: #faf5ff; color: #7e22ce; border: 1px solid #f3e8ff;">CRM Task</span>`
          : `<span style="color: #64748b; font-weight: 500;">${phaseName}</span>`;
        const desc = entry.content || '—';
        const duration = this.getDuration(entry.startTime, entry.endTime);
        const statusText = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED' ? 'Approuvé' : 'Brouillon';
        const statusBadge = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED'
          ? `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 600; background-color: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0;"><span style="width: 5px; height: 5px; border-radius: 50%; background-color: #22c55e;"></span>${statusText}</span>`
          : `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 600; background-color: #f8fafc; color: #64748b; border: 1px solid #e2e8f0;"><span style="width: 5px; height: 5px; border-radius: 50%; background-color: #cbd5e1;"></span>${statusText}</span>`;

        tableRowsHtml += `
          <tr style="page-break-inside: avoid; border-bottom: 1px solid #f1f5f9;">
            <td style="padding: 12px 10px; color: #64748b; font-family: monospace;">${dateStr}</td>
            ${!this.selectedEmployeeId ? `<td style="padding: 12px 10px; font-weight: 600; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 110px;">${empName}</td>` : ''}
            <td style="padding: 12px 10px; font-weight: 600; color: #1e293b; max-width: 180px; word-break: break-word;">${projName}</td>
            <td style="padding: 12px 10px; color: #475569; max-width: 150px; word-break: break-word;">${typeBadge}</td>
            <td style="padding: 12px 10px; color: #64748b; font-style: italic; max-width: 250px; word-break: break-word;">${desc}</td>
            <td style="padding: 12px 10px; text-align: right; font-weight: 700; color: #1e293b; font-family: monospace;">${duration}</td>
            <td style="padding: 12px 10px; text-align: center;">${statusBadge}</td>
          </tr>
        `;
      });
    }

    // Generate project breakdown HTML
    let projectsBreakdownHtml = '';
    const projectBreakdownsList = this.getBreakdownsForType('project');
    if (projectBreakdownsList.length === 0) {
      projectsBreakdownHtml = `<p style="font-size: 11px; color: #94a3b8; font-style: italic; margin: 0; padding: 12px 0;">Aucun projet.</p>`;
    } else {
      projectBreakdownsList.forEach(pb => {
        const percent = this.totalHours > 0 ? Math.round((pb.hours / this.totalHours) * 100) : 0;
        projectsBreakdownHtml += `
          <div style="margin-bottom: 10px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 3px;">
              <div style="display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1;">
                <div style="width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; background-color: ${pb.color};"></div>
                <span style="font-size: 11px; font-weight: 600; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${pb.name}</span>
              </div>
              <span style="font-size: 11px; font-weight: bold; color: #0f172a; font-family: monospace; margin-left: 8px;">${this.formatHours(pb.hours)}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="flex: 1; height: 5px; background-color: #f1f5f9; border-radius: 9999px; overflow: hidden;">
                <div style="height: 100%; width: ${percent}%; background-color: ${pb.color}; border-radius: 9999px;"></div>
              </div>
              <span style="font-size: 10px; color: #94a3b8; font-weight: bold; width: 28px; text-align: right; font-family: monospace;">${percent}%</span>
            </div>
          </div>
        `;
      });
    }

    // Generate phase breakdown HTML
    let phasesBreakdownHtml = '';
    const phaseBreakdownsList = this.getBreakdownsForType('phase');
    if (phaseBreakdownsList.length === 0) {
      phasesBreakdownHtml = `<p style="font-size: 11px; color: #94a3b8; font-style: italic; margin: 0; padding: 12px 0;">Aucune phase.</p>`;
    } else {
      phaseBreakdownsList.forEach(pb => {
        const percent = this.totalHours > 0 ? Math.round((pb.hours / this.totalHours) * 100) : 0;
        phasesBreakdownHtml += `
          <div style="margin-bottom: 10px;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 3px;">
              <div style="display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1;">
                <div style="width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; background-color: ${pb.color};"></div>
                <span style="font-size: 11px; font-weight: 600; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${pb.name}</span>
              </div>
              <span style="font-size: 11px; font-weight: bold; color: #0f172a; font-family: monospace; margin-left: 8px;">${this.formatHours(pb.hours)}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="flex: 1; height: 5px; background-color: #f1f5f9; border-radius: 9999px; overflow: hidden;">
                <div style="height: 100%; width: ${percent}%; background-color: ${pb.color}; border-radius: 9999px;"></div>
              </div>
              <span style="font-size: 10px; color: #94a3b8; font-weight: bold; width: 28px; text-align: right; font-family: monospace;">${percent}%</span>
            </div>
          </div>
        `;
      });
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport de Prestations - SEL Studio</title>
        <meta charset="utf-8">
        ${stylesHtml}
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
        <style>
          @media print {
            @page {
              size: landscape;
              margin: 12mm;
            }
            body {
              background-color: white !important;
              color: #0f172a !important;
            }
            .page-break {
              page-break-after: always;
              break-after: page;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            margin: 0;
            padding: 0;
            background-color: #ffffff;
            color: #1e293b;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #f1f5f9;
            padding-bottom: 16px;
            margin-bottom: 20px;
          }
          .logo-container {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
          }
          .logo-box {
            width: 28px;
            height: 28px;
            border-radius: 6px;
            background-color: #2563eb;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 14px;
          }
          .logo-text {
            font-size: 16px;
            font-weight: 700;
            letter-spacing: -0.025em;
            color: #0f172a;
          }
          .report-title {
            font-size: 20px;
            font-weight: 700;
            color: #1e293b;
            margin: 0;
          }
          .period-subtitle {
            font-size: 12px;
            color: #64748b;
            margin: 4px 0 0 0;
          }
          .meta-right {
            text-align: right;
            font-size: 11px;
          }
          .meta-label {
            font-size: 9px;
            font-weight: 700;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          .meta-value {
            font-weight: 600;
            color: #334155;
            margin-top: 2px;
          }
          .filters-summary {
            margin-top: 8px;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 2px;
            font-size: 11px;
            color: #64748b;
          }
          .section-title {
            font-size: 11px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-top: 0;
            margin-bottom: 12px;
          }
          .footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 9px;
            color: #94a3b8;
            font-weight: 500;
          }
        </style>
      </head>
      <body>
        <div style="width: 100%; max-width: 1060px; margin: 0 auto; padding: 10px;">
          
          <!-- ================= PAGE 1: SYNTHÈSE / EXECUTIVE SUMMARY ================= -->
          <div class="page-break" style="margin-bottom: 24px;">
            <!-- Header -->
            <div class="header">
              <div>
                <div class="logo-container">
                  <div class="logo-box">S</div>
                  <span class="logo-text">SEL STUDIO</span>
                </div>
                <h1 class="report-title">Rapport de Prestations</h1>
                <p class="period-subtitle">Période : <strong>${this.periodLabel}</strong></p>
              </div>
              <div class="meta-right">
                <div class="meta-label">Généré le</div>
                <div class="meta-value">${dateFormatted}</div>
                <div class="filters-summary">
                  <div>Employé : <strong>${employee}</strong></div>
                  <div>Statut : <strong>${status}</strong></div>
                </div>
              </div>
            </div>

            <!-- Page 1 Grid: Large KPIs -->
            <h2 class="section-title">Synthèse Globale de l'Activité</h2>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px;">
              <!-- KPI 1 -->
              <div class="card" style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; background-color: #f8fafc; border: 1px solid #f1f5f9; border-radius: 12px;">
                <div class="meta-label" style="font-size: 10px; margin-bottom: 4px;">Total des heures</div>
                <div style="font-size: 32px; font-weight: 800; color: #2563eb; font-family: monospace;">${this.formatHours(this.totalHours)}</div>
                <div style="font-size: 10px; color: #94a3b8; margin-top: 4px; font-weight: 600;">${sortedEntries.length} prestations enregistrées</div>
              </div>
              <!-- KPI 2 -->
              <div class="card" style="display: flex; flex-direction: column; justify-content: center; padding: 20px; background-color: #f8fafc; border: 1px solid #f1f5f9; border-radius: 12px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                  <span class="meta-label" style="font-size: 10px; display: flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background-color: #22c55e;"></span>Approuvées</span>
                  <span style="font-size: 14px; font-weight: bold; color: #1e293b; font-family: monospace;">${this.formatHours(this.approvedTotal)}</span>
                </div>
                <div style="width: 100%; height: 8px; background-color: #e2e8f0; border-radius: 9999px; overflow: hidden; margin-bottom: 4px;">
                  <div style="height: 100%; width: ${this.totalHours > 0 ? (this.approvedTotal / this.totalHours * 100) : 0}%; background-color: #22c55e; border-radius: 9999px;"></div>
                </div>
                <div style="font-size: 10px; color: #64748b; font-weight: 500; text-align: right;">${this.totalHours > 0 ? Math.round((this.approvedTotal / this.totalHours) * 100) : 0}% du total</div>
              </div>
              <!-- KPI 3 -->
              <div class="card" style="display: flex; flex-direction: column; justify-content: center; padding: 20px; background-color: #f8fafc; border: 1px solid #f1f5f9; border-radius: 12px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                  <span class="meta-label" style="font-size: 10px; display: flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background-color: #fbbf24;"></span>Brouillons</span>
                  <span style="font-size: 14px; font-weight: bold; color: #1e293b; font-family: monospace;">${this.formatHours(this.draftTotal)}</span>
                </div>
                <div style="width: 100%; height: 8px; background-color: #e2e8f0; border-radius: 9999px; overflow: hidden; margin-bottom: 4px;">
                  <div style="height: 100%; width: ${this.totalHours > 0 ? (this.draftTotal / this.totalHours * 100) : 0}%; background-color: #fbbf24; border-radius: 9999px;"></div>
                </div>
                <div style="font-size: 10px; color: #64748b; font-weight: 500; text-align: right;">${this.totalHours > 0 ? Math.round((this.draftTotal / this.totalHours) * 100) : 0}% du total</div>
              </div>
            </div>

            <!-- Page 1 Grid: Project vs Phase Breakdowns -->
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px;">
              <!-- Left Column: Projects -->
              <div style="border: 1px solid #f1f5f9; border-radius: 12px; padding: 20px; background-color: white;">
                <h3 class="section-title" style="font-size: 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #f8fafc; padding-bottom: 8px; margin-bottom: 12px;">Répartition par Projet</h3>
                ${projectsBreakdownHtml}
              </div>
              <!-- Right Column: Phases -->
              <div style="border: 1px solid #f1f5f9; border-radius: 12px; padding: 20px; background-color: white;">
                <h3 class="section-title" style="font-size: 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #f8fafc; padding-bottom: 8px; margin-bottom: 12px;">Répartition par Phase / Tâche</h3>
                ${phasesBreakdownHtml}
              </div>
            </div>

            <!-- Footer P1 -->
            <div class="footer" style="margin-top: 32px; border-top: 1px solid #f1f5f9; padding-top: 12px;">
              <div>Document de synthèse officielle - SEL Studio</div>
              <div>Page 1 sur ${sortedEntries.length > 0 ? 2 : 1}</div>
            </div>
          </div>

          <!-- ================= PAGE 2+: JOURNAL DÉTAILLÉ (DETAILED LEDGER) ================= -->
          ${sortedEntries.length > 0 ? `
          <div class="detailed-table-page">
            <!-- P2 Header -->
            <div class="header">
              <div>
                <div class="logo-container">
                  <div class="logo-box" style="background-color: #475569;">S</div>
                  <span class="logo-text" style="color: #475569;">SEL STUDIO</span>
                </div>
                <h1 class="report-title" style="font-size: 16px; color: #475569;">Journal Détaillé des Prestations</h1>
                <p class="period-subtitle">Période : <strong>${this.periodLabel}</strong></p>
              </div>
              <div class="meta-right" style="font-size: 11px;">
                <div>Employé : <strong>${employee}</strong></div>
                <div>Statut : <strong>${status}</strong></div>
              </div>
            </div>

            <div style="border: 1px solid #f1f5f9; border-radius: 12px; overflow: hidden; background-color: white; margin-top: 16px;">
              <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                <thead>
                  <tr style="background-color: #f8fafc; border-bottom: 1px solid #f1f5f9;">
                    <th style="padding: 12px 10px; width: 10%; text-align: left; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Date</th>
                    ${!this.selectedEmployeeId ? '<th style="padding: 12px 10px; width: 15%; text-align: left; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Employé</th>' : ''}
                    <th style="padding: 12px 10px; width: 22%; text-align: left; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Projet</th>
                    <th style="padding: 12px 10px; width: 18%; text-align: left; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Phase / Type</th>
                    <th style="padding: 12px 10px; width: 25%; text-align: left; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Description</th>
                    <th style="padding: 12px 10px; width: 10%; text-align: right; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Durée</th>
                    <th style="padding: 12px 10px; width: 10%; text-align: center; font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase;">Statut</th>
                  </tr>
                </thead>
                <tbody style="color: #475569;">
                  ${tableRowsHtml}
                </tbody>
              </table>
            </div>

            <!-- Footer P2 -->
            <div class="footer" style="margin-top: 24px; border-top: 1px solid #f1f5f9; padding-top: 12px;">
              <div>Journal des activités détaillé - Généré depuis SEL Studio</div>
              <div>Page 2 sur 2</div>
            </div>
          </div>
          ` : ''}

        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
}
